import { ApplicationStatusSchema, type TApplicationStatus } from '@doutorsim/shared/applications'
import type { TPaginationMeta } from '@doutorsim/shared/pagination'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { toArray } from '@/_helpers/to-array'
import { getKanbanColumnId } from '@/features/_constants/admission-status'
import { api } from '@/features/_lib/api'
import type { TStaffApplicationListItem } from '@/features/_lib/api/entities/applications.staff'
import { queries } from '@/features/_lib/react-query'
import type { ApplicationsQueryFilters } from './use-applications-query'

export type ColumnData = {
  data: TStaffApplicationListItem[]
  meta: TPaginationMeta
}

export const LIMIT_PER_STATUS = 10
const LOAD_MORE_LIMIT = 10
const EMPTY_COLUMN: ColumnData = { data: [], meta: { hasMore: false } }

function parseStatus(value: string): TApplicationStatus | null {
  const result = ApplicationStatusSchema.safeParse(value)
  return result.success ? result.data : null
}

export function useKanbanCardsQuery({
  mode,
  companyId,
  facilityId,
  search,
  status,
  startDate,
  endDate,
  filterFacilityIds,
}: ApplicationsQueryFilters) {
  // "See more" pages per column
  const [extras, setExtras] = useState<
    Record<string, { data: TStaffApplicationListItem[]; meta: TPaginationMeta }>
  >({})
  const [fetchingStatuses, setFetchingStatuses] = useState<Set<string>>(new Set())

  // Staff: fetch ALL statuses on mount. Only search/date trigger refetches.
  // Status filter is purely UI (show/hide columns).
  const staffQuery = useQuery({
    ...queries.staffApplicationCards.all({
      limitPerStatus: LIMIT_PER_STATUS,
      search,
      startDate,
      endDate,
      facilityIds: filterFacilityIds,
    }),
    enabled: mode === 'staff',
  })

  // Facility: flat list scoped to a single facility
  const facilityQuery = useQuery({
    ...queries.applications.list(companyId ?? '', facilityId ?? '', {
      search,
      status: toArray(status),
      startDate,
      endDate,
      limit: 100,
    }),
    enabled: mode === 'facility' && !!facilityId,
  })

  // Company (tenant): flat list across the company's facilities
  const companyQuery = useQuery({
    ...queries.companyApplications.list(companyId ?? '', {
      search,
      status: toArray(status),
      startDate,
      endDate,
      limit: 100,
    }),
    enabled: mode === 'company' && !!companyId,
  })

  // Both tenant modes (company/facility) return a flat list grouped client-side.
  const tenantQuery = mode === 'company' ? companyQuery : facilityQuery

  // Clear "see more" pages when the base query refetches
  const prevDataUpdatedAtRef = useRef<number>(0)
  useEffect(() => {
    if (staffQuery.dataUpdatedAt && staffQuery.dataUpdatedAt !== prevDataUpdatedAtRef.current) {
      prevDataUpdatedAtRef.current = staffQuery.dataUpdatedAt
      setExtras({})
    }
  }, [staffQuery.dataUpdatedAt])

  // Merge base data + "see more" extras
  const columns = useMemo<Record<string, ColumnData>>(() => {
    if (mode === 'staff' && staffQuery.data) {
      const result: Record<string, ColumnData> = {}

      for (const [statusKey, bucket] of Object.entries(staffQuery.data)) {
        const extra = extras[statusKey]
        if (extra) {
          result[statusKey] = {
            data: [...bucket.data, ...extra.data],
            meta: extra.meta,
          }
        } else {
          result[statusKey] = bucket
        }
      }

      return result
    }

    if (mode !== 'staff' && tenantQuery.data) {
      const flat = tenantQuery.data.data ?? []
      const result: Record<string, ColumnData> = {}

      // Bucket by the tenant column id (group representative), not the raw
      // status — the tenant board renders one column per group, so cards keyed
      // by a non-representative raw status would match no column and vanish.
      for (const app of flat) {
        const columnId = getKanbanColumnId(app.status, 'tenant')
        const column = result[columnId] ?? { data: [], meta: { hasMore: false, totalCount: 0 } }
        column.data.push(app)
        result[columnId] = column
      }

      for (const col of Object.values(result)) {
        col.meta.totalCount = col.data.length
      }

      return result
    }

    return {}
  }, [mode, staffQuery.data, tenantQuery.data, extras])

  const getColumn = useCallback(
    (columnId: string): ColumnData => columns[columnId] ?? EMPTY_COLUMN,
    [columns],
  )

  const isLoading = mode === 'staff' ? staffQuery.isLoading : tenantQuery.isLoading
  const isFetching = mode === 'staff' ? staffQuery.isFetching : tenantQuery.isFetching

  // Track previous card counts per column so skeletons match during refetch
  const prevColumnCountsRef = useRef<Record<string, number>>({})
  useEffect(() => {
    if (!isFetching) {
      const counts: Record<string, number> = {}
      for (const [key, col] of Object.entries(columns)) {
        counts[key] = col.data.length
      }
      prevColumnCountsRef.current = counts
    }
  }, [isFetching, columns])

  // "See more" per column
  const fetchMore = useCallback(
    async (columnId: string) => {
      if (mode !== 'staff') return

      const parsedStatus = parseStatus(columnId)
      if (!parsedStatus) return

      const column = columns[columnId]
      if (!column?.meta?.hasMore || !column.meta.nextCursor) return

      setFetchingStatuses((prev) => new Set(prev).add(columnId))

      try {
        const result = await api.staff.applications.getApplicationCardsByStatus(parsedStatus, {
          limit: LOAD_MORE_LIMIT,
          cursor: column.meta.nextCursor,
          direction: 'forward',
          search,
          startDate,
          endDate,
          facilityIds: filterFacilityIds,
        })

        setExtras((prev) => {
          const existing = prev[columnId]
          return {
            ...prev,
            [columnId]: {
              data: [...(existing?.data ?? []), ...result.data],
              meta: result.meta,
            },
          }
        })
      } finally {
        setFetchingStatuses((prev) => {
          const next = new Set(prev)
          next.delete(columnId)
          return next
        })
      }
    },
    [mode, columns, search, startDate, endDate, filterFacilityIds],
  )

  const isFetchingMore = useCallback(
    (columnId: string) => fetchingStatuses.has(columnId),
    [fetchingStatuses],
  )

  return {
    columns,
    getColumn,
    isLoading,
    isFetching,
    prevColumnCounts: prevColumnCountsRef.current,
    fetchMore,
    isFetchingMore,
  }
}
